import { ATTR_IDENTIFIER, PRODUCT_TYPE } from "../constants/catalog";
import { DISPLAY, EMPTY_STRING, OFFER } from "../constants/common";
import { isUseableData, preciseCalculate, truncateNumberToDecimals } from "./genericUtil";
import { getDateMonth } from "./storeLocatorUtils";
import storeUtil from "./storeUtil";

/**
 * Set the product offer price and display price
 * @param priceArray
 */
export const getPrices = (priceArray: any[] | null) => {
  // Guard: only handle real arrays; anything else → no prices
  if (priceArray) {
    let offer: string = "0";
    let list: string = "0";
    for (const price of priceArray) {
      if (price.usage === OFFER && price.value !== EMPTY_STRING) {
        offer = price.value;
      } else if (price.usage === DISPLAY && price.value !== EMPTY_STRING) {
        list = price.value;
      }
    }
    return { offer: parseFloat(offer), list: parseFloat(list) };
  }
  return { offer: 0, list: 0 };
};

export const getPrice = (priceArray: any[] | null) => {
  if (priceArray) {
    let offer: string = "0";
    let list: string = "0";
    for (const price of priceArray) {
      if (price.usage === OFFER && price.value !== EMPTY_STRING) {
        offer = price.value;
      } else if (price.usage === DISPLAY && price.value !== EMPTY_STRING) {
        list = price.value;
      }
    }
    return offer !== "0" ? parseFloat(offer) : parseFloat(list);
  }
  return 0;
};

/**
 * Caculate the TVA from a price and TVA percentage
 * @param {number} price - product price
 * @param {number} [percent=20] - taxe percentage %
 */
export const getTvaPrice = (price: number, percent: number = 20) => {
  return +Number((price * percent) / 100).toFixed(2) || 0;
};

type RemisedPriceObj = {
  oldPrice: number | null;
  OldPriceEnd: string | null;
  valid: boolean;
} | null;

interface ProductPriceReturnData {
  markedPrice: number | null;
  promoPrice: number | null;
  priceDifference: number | null;
  promoPercentage: number | null;
  ecoPart: number | null;
  meterPrice: number | null;
  meterSize: number | null;
  promotionId: string | null;
  promotionName: string | null;
  type: string | null;
  isPromo: boolean;
  remisedPriceDetails: RemisedPriceObj;
}

export const getProductPriceData = (product: Record<any, any>): ProductPriceReturnData => {
  let markedPrice: number | null = null;
  let promoPrice: number | null = null;
  let priceDifference: number | null = null;
  let promoPercentage: number | null = null;
  let ecoPart: number | null = null;
  let meterPrice: number | null = null;
  let meterSize: number | null = null;

  let promotionId: string | null = null;
  let promotionName: string | null = null;
  let type: string | null = null;

  let isPromo: boolean = false;
  let remisedPriceDetails: RemisedPriceObj = null;

  if (typeof product === "object" && product) {
    const prices = getPrices(product?.price);
    markedPrice = Number(prices.list);

    if ("promoPriceData.numeric" in product && Number(product["promoPriceData.numeric"]) < markedPrice) {
      promoPrice = Number(product["promoPriceData.numeric"]);
      priceDifference = truncateNumberToDecimals(markedPrice - promoPrice);
      if (priceDifference > 0) {
        isPromo = true;
      }
    }
    if ("promoPercentage.numeric" in product && Number(product["promoPercentage.numeric"]) > 0) {
      promoPercentage = Number(product["promoPercentage.numeric"]);
    }
    if ("ecoPriceData.numeric" in product && Number(product["ecoPriceData.numeric"]) > 0) {
      ecoPart = Number(product["ecoPriceData.numeric"]);
    }
    if ("boxPriceData.numeric" in product && Number(product["boxPriceData.numeric"]) > 0) {
      meterPrice = Number(product["boxPriceData.numeric"]);
    }

    if ("promotionId.raw" in product && isUseableData(product["promotionId.raw"])) {
      promotionId = product["promotionId.raw"];
    }
    if ("promotionName.raw" in product && isUseableData(product["promotionName.raw"])) {
      promotionName = product["promotionName.raw"];
    }
    if (product?.attributes) {
      type = storeUtil.getTypeProduct(product?.attributes || [], product) || null;
    }

    if (product?.attributes && type) {
      meterSize = truncateNumberToDecimals(Number(storeUtil.getSalesUnitProduct(product.attributes, type)), 3);
    }
    if (product?.attributes &&!meterSize&& meterSize === 0 && product?.localQuantity) {
      const productType = storeUtil.getTypeProduct(product?.attributes, product) || null;
      const isM2product: boolean = productType === PRODUCT_TYPE.COLISE || productType === PRODUCT_TYPE.BOTTE;
      meterSize = (isM2product && Number(1 / product?.xitem_COEFF_CONVERSION)) || null;
    }
    if (product?.attributes) {
      const remisedPriceObj = { oldPrice: "", OldPriceEnd: "" };
      for (const att of product.attributes) {
        if (att.identifier === ATTR_IDENTIFIER.OLD_PRICE) {
          remisedPriceObj.oldPrice = att?.values[0]?.value;
        }
        if (att.identifier === ATTR_IDENTIFIER.OLD_PRICE_END) {
          remisedPriceObj.OldPriceEnd = att?.values[0]?.value.replaceAll("-", "/");
        }
      }
      if ("custom.oldPrice.raw" in product && isUseableData(product["custom.oldPrice.raw"])) {
        remisedPriceObj.oldPrice = product["custom.oldPrice.raw"];
      }
      if ("custom.oldPriceEndDate.raw" in product && isUseableData(product["custom.oldPriceEndDate.raw"])) {
        remisedPriceObj.OldPriceEnd = product["custom.oldPriceEndDate.raw"];
      }
      const remisedPrice = Number(remisedPriceObj.oldPrice) || null;
      const remisedPriceEnd = getDateMonth(remisedPriceObj.OldPriceEnd, true, "DD/MM/YYYY") || null;
      remisedPriceDetails = {
        oldPrice: remisedPrice,
        OldPriceEnd: remisedPriceEnd,
        valid: remisedPrice ? markedPrice < remisedPrice : false,
      };
    }
    if (product?.localQuantity) {
      /*
        this is a special case where,
        orderItem here already contains aggregated quantities (in quantity key) but each time in cart confirm modal we need to show prices
        in respect to local quantity being added in the cart, hence taking data from localQuantity key, otherwise all prices above contain
        unit price data and price for quantity of items already. So otherwise no need to manually multiply by quantity.
      */
      const localQty = Number(product?.localQuantity);
      markedPrice = truncateNumberToDecimals(markedPrice * localQty);
      promoPrice = promoPrice && truncateNumberToDecimals(promoPrice * localQty);
      ecoPart = ecoPart && truncateNumberToDecimals(ecoPart * localQty);
    }
  }

  return {
    markedPrice,
    promoPrice,
    priceDifference,
    promoPercentage,
    ecoPart,
    meterPrice,
    meterSize,
    promotionId,
    promotionName,
    type,
    isPromo,
    remisedPriceDetails,
  };
};

interface OrderPriceReturnData {
  markedPrice: number | null;
  promoPrice: number | null;
  unitMarkedPricePerOneM2: number | null;
  unitPromoPricePerOneM2: number | null;
  unitMarkedPrice: number | null;
  unitPromoPrice: number | null;
  unitEcoPart: number | null;
  ecoPart: number | null;
  promoPercentage: number | null;
  meterSize: number | null;
  meterPrice: number | null;
  productType: string | null;
  isPromo: boolean;
}

export const calculateOrderItemPriceData = (orderItem: Record<any, any>): OrderPriceReturnData => {
  let markedPrice: number | null = null;
  let promoPrice: number | null = null;
  let unitMarkedPricePerOneM2: number | null = null;
  let unitPromoPricePerOneM2: number | null = null;
  let unitMarkedPrice: number | null = null;
  let unitPromoPrice: number | null = null;
  let unitEcoPart: number | null = null;
  let ecoPart: number | null = null;
  let promoPercentage: number | null = null;
  let meterSize: number | null = null;
  let meterPrice: number | null = null;

  let productType: string | null = null;

  let isPromo: boolean = false;

  if (orderItem) {
    productType = storeUtil.getTypeProduct(orderItem?.attributes, orderItem) || null;

    const isM2product: boolean = productType === PRODUCT_TYPE.COLISE || productType === PRODUCT_TYPE.BOTTE;
    meterSize = (isM2product && truncateNumberToDecimals(Number(1 / orderItem?.xitem_COEFF_CONVERSION))) || null;
    meterPrice = (isM2product && Number(orderItem?.boxPrice)) || null;

    unitEcoPart = Number(orderItem?.xitem_ECOPART_TTC) || null;
    ecoPart = Number(orderItem?.xitem_TOTALECOPART_TTC) || null;

    let totalAdjustmentValueforOne: number = 0;
    // Use localQuantity if present, otherwise fall back to quantity or 1
    const adjQty = (orderItem && Object.prototype.hasOwnProperty.call(orderItem, 'localQuantity'))
        ? Number(orderItem.localQuantity)
      : (orderItem.quantity !== undefined ? Number(orderItem.quantity) : 1);
    if (orderItem?.adjustment?.length > 0) {
      orderItem.adjustment.forEach((adjustmentItem: Record<string, any>) => {
        if (adjustmentItem.displayLevel === "OrderItem") {
          const unitAdj = Number(adjustmentItem?.xadju_unitLevelAdjustment || adjustmentItem?.xiadju_unitLevelAdjustment);
          totalAdjustmentValueforOne = totalAdjustmentValueforOne + (unitAdj * adjQty);
        }
      });
    }

    // NOTE: for m2 products getPrices() / orderItem?.price is added manually from products API and it contains prices per ONE meter square but
    // unitPrice + unitEcoPart gives us price per sales unit (meterSize) meter sqaure.
    if (isM2product) {
      unitMarkedPricePerOneM2 = Number(getPrices(orderItem?.price)?.list) || null;
      unitPromoPricePerOneM2 = Number(getPrices(orderItem?.price)?.offer) || null; //there can be old, not updated data in this value
    }
    unitMarkedPrice = Number(orderItem?.unitPrice) + Number(unitEcoPart || 0);
    unitPromoPrice = meterPrice || preciseCalculate([unitMarkedPrice, totalAdjustmentValueforOne]) || null;

    /*
      this is a special case where,
      orderItem here already contains aggregated quantities (in quantity key) but each time in cart confirm modal we need to show prices
      in respect to local quantity being added in the cart, hence taking data from localQuantity key first before quantity in orderItem
    */
    const quantity = Number(orderItem?.localQuantity) || Number(orderItem?.quantity);
    markedPrice = unitMarkedPrice ? truncateNumberToDecimals(unitMarkedPrice * quantity) : null;
    promoPrice = unitPromoPrice ? truncateNumberToDecimals(unitPromoPrice * quantity) : null;
    ecoPart = unitEcoPart ? truncateNumberToDecimals(unitEcoPart * quantity) : null;

    if (markedPrice && promoPrice && unitMarkedPrice && unitPromoPrice) {
      isPromo = markedPrice - promoPrice > 0 || unitMarkedPrice - unitPromoPrice > 0;
    }
    promoPercentage = Number(storeUtil.getPromotionalPercentage(orderItem)) || null;
  }

  return {
    markedPrice,
    promoPrice,
    unitMarkedPricePerOneM2,
    unitPromoPricePerOneM2,
    unitMarkedPrice,
    unitPromoPrice,
    unitEcoPart,
    ecoPart,
    promoPercentage,
    meterSize,
    meterPrice,
    productType,
    isPromo,
  };
};

export const getOrderItemPriceData = (orderItem: Record<any, any>): OrderPriceReturnData => {
  let markedPrice: number | null = null;
  let promoPrice: number | null = null;
  let unitMarkedPricePerOneM2: number | null = null;
  let unitPromoPricePerOneM2: number | null = null;
  let unitMarkedPrice: number | null = null;
  let unitPromoPrice: number | null = null;
  let unitEcoPart: number | null = null;
  let ecoPart: number | null = null;
  let promoPercentage: number | null = null;
  let meterSize: number | null = null;
  let meterPrice: number | null = null;

  let productType: string | null = null;

  let isPromo: boolean = false;

  if (orderItem) {
    productType = storeUtil.getTypeProduct(orderItem?.attributes, orderItem) || null;

    const isM2product: boolean = productType === PRODUCT_TYPE.COLISE || productType === PRODUCT_TYPE.BOTTE;
    meterSize = (isM2product && Number(1 / orderItem?.xitem_COEFF_CONVERSION)) || null;
    meterPrice = (isM2product && Number(orderItem?.boxPrice)) || null;

    unitMarkedPricePerOneM2 = Number(orderItem?.xitem_m2UnitlistPrice) || null; //per ONE meter sq. (diff from per sales unit meter sq.)
    unitPromoPricePerOneM2 = Number(orderItem?.xitem_m2UnitOfferPrice) || null; //per ONE meter sq. (diff from per sales unit meter sq.)

    unitMarkedPrice = Number(orderItem?.xitem_unitListPrice) || null;
    unitPromoPrice = Number(orderItem?.xitem_unitOfferPrice) || null;

    markedPrice = Number(orderItem?.xitem_listPrice) || null;
    promoPrice = Number(orderItem?.xitem_offerPrice) || null;

    unitEcoPart = Number(orderItem?.xitem_ECOPART_TTC) || null;
    ecoPart = Number(orderItem?.xitem_TOTALECOPART_TTC) || null;

    // Always use localQuantity for price calculation if present (even if 0), never use quantity if localQuantity is defined
    let qty = 1;
    const hasLocalQuantity = Object.prototype.hasOwnProperty.call(orderItem, 'localQuantity');
    if (hasLocalQuantity) {
      qty = Number(orderItem.localQuantity);
    } else if (orderItem.quantity !== undefined) {
      qty = Number(orderItem.quantity);
    }
    // Always use unit price fields for calculation if available
    if (unitMarkedPrice) {
      markedPrice = truncateNumberToDecimals(unitMarkedPrice * qty);
    }
    if (unitPromoPrice) {
      promoPrice = truncateNumberToDecimals(unitPromoPrice * qty);
    }
    if (unitEcoPart) {
      ecoPart = truncateNumberToDecimals(unitEcoPart * qty);
    }
    // Fallback for cases where unit prices are not available
    if ((!unitMarkedPrice || !unitPromoPrice) && markedPrice && promoPrice) {
      markedPrice = truncateNumberToDecimals(markedPrice / (orderItem.quantity || 1) * qty);
      promoPrice = truncateNumberToDecimals(promoPrice / (orderItem.quantity || 1) * qty);
    }
    if (!unitEcoPart && ecoPart) {
      ecoPart = truncateNumberToDecimals(ecoPart / (orderItem.quantity || 1) * qty);
    }

    promoPercentage = Number(storeUtil.getPromotionalPercentage(orderItem)) || null;
    if (markedPrice && promoPrice && unitMarkedPrice && unitPromoPrice) {
      isPromo = markedPrice - promoPrice > 0 || unitMarkedPrice - unitPromoPrice > 0;
    }
  }

  return {
    markedPrice,
    promoPrice,
    unitMarkedPricePerOneM2,
    unitPromoPricePerOneM2,
    unitMarkedPrice,
    unitPromoPrice,
    unitEcoPart,
    ecoPart,
    promoPercentage,
    meterSize,
    meterPrice,
    productType,
    isPromo,
  };
};

/**
 * Formats a price value to French locale format (e.g. 9.9 → "9,9")
 * @param value - The price as a number or string
 * @returns French-formatted price string
 */
export const formatPriceFR = (value: number | string): string => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return String(value).replace(".", ",");
  return num.toFixed(2).replace(".", ",");
};
